import { RefObject, useEffect, useRef } from 'react';
import { EXTERNAL_APP_CLOSE_MESSAGE } from '../constants';
import { runResponseActions } from '../utils/apiRequests/runResponseActions';
import { ResponseActionHandler } from '../types/context';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('controlActions');

export type UseExternalAppMessagesParams = {
  /** Caller-verified origin; messages accepted from it only, undefined binds no listener. */
  allowedOrigin: string | undefined;
  /** The embedding iframe — messages are accepted from its window only. */
  iframeRef: RefObject<HTMLIFrameElement | null>;
  getResponseActionHandler: (name: string) => ResponseActionHandler | undefined;
  onClose: () => void;
};

// Handles the embed's 'app:close' — runs its responseActions, then navigates back.
export const useExternalAppMessages = ({
  allowedOrigin,
  iframeRef,
  getResponseActionHandler,
  onClose,
}: UseExternalAppMessagesParams) => {
  // Listener binds once per origin; only the callbacks flow through the ref.
  const callbacksRef = useRef({ getResponseActionHandler, onClose });
  useEffect(() => {
    callbacksRef.current = { getResponseActionHandler, onClose };
  });

  useEffect(() => {
    if (!allowedOrigin) {
      return;
    }

    const handler = async (event: MessageEvent) => {
      if (event.origin !== allowedOrigin) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      const data = event.data as {
        type?: string;
        responseActions?: Record<string, unknown>;
      } | null;
      if (data?.type !== EXTERNAL_APP_CLOSE_MESSAGE) {
        return;
      }

      /** @debugFeature controlActions Logs an app:close message received from the embedded external app: its origin and the responseActions it requested (if any). */
      log('external app close message received', () => ({
        origin: event.origin,
        responseActions: data.responseActions,
      }));

      // Actions run first — their handlers live in contexts that outlive this page.
      try {
        await runResponseActions(
          data.responseActions,
          {},
          callbacksRef.current.getResponseActionHandler
        );
      } catch (error) {
        // A malformed embed payload must never block the close itself.
        console.warn('External app responseActions failed', error);
      }
      callbacksRef.current.onClose();
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [allowedOrigin, iframeRef]);
};
